import {
  AfterViewInit,
  Component,
  DestroyRef,
  QueryList,
  ViewChildren,
  inject,
  viewChild,
  input,
  computed,
  Signal,
  ChangeDetectionStrategy,
  InputSignal,
} from '@angular/core';
import { filter, startWith } from 'rxjs/operators';
import { AlfredClientService } from '../../../alfred-client.service';
import { DynamicComponentLoader } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DisplayMathPipe,
  QuestionTableDataContainer,
  QuestionTableService,
  QuestionTableComponent,
  QuestionDisplayPlaceholderDirective,
  QuestionPlaceholderDirective,
} from '@lyceo/shared/question-display/feature-main';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-question-display',
  templateUrl: './question-display.component.html',
  styleUrls: ['./question-display.component.scss'],
  imports: [QuestionDisplayPlaceholderDirective, DisplayMathPipe],
})
export class QuestionDisplayComponent implements AfterViewInit {
  public alfredClientService: AlfredClientService = inject(AlfredClientService);
  public dynamicComponentLoader: DynamicComponentLoader = inject(
    DynamicComponentLoader,
  );
  public questionTableService: QuestionTableService =
    inject(QuestionTableService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * The question to display
   */
  public readonly question: InputSignal<string> = input<string>('');

  /**
   * The processed question to display.
   */
  public readonly _question: Signal<string> = computed(() => {
    let question: string = this.question();

    if (
      typeof question !== 'string' &&
      this.alfredClientService.isDirectHandleMode()
    ) {
      question = '<i>[hier komt de vraag]</i>';
    }

    // set up question tables
    if (typeof question === 'string') {
      this.questionTableDataContainer =
        this.questionTableService.tablesJsonToQuestionTableData(question);
      question = this.questionTableDataContainer.questionWithTablePlaceholders;
    }

    return question;
  });

  /**
   * The question display placeholder QueryList.
   */
  //eslint-disable-next-line @angular-eslint/prefer-signals
  @ViewChildren(QuestionDisplayPlaceholderDirective)
  public questionDisplayPlaceholderList: QueryList<QuestionDisplayPlaceholderDirective> =
    new QueryList();

  /**
   * The question display placeholder will be used to display the correct question.
   */
  public questionDisplayPlaceholder!: QuestionDisplayPlaceholderDirective;

  /**
   * Data for tables used in the question.
   */
  private questionTableDataContainer!: QuestionTableDataContainer;

  /**
   * The question placeholder will be used to place the correct question
   * specific component inside by dynamic component loading.
   */
  private readonly questionPlaceholder: Signal<QuestionPlaceholderDirective> =
    viewChild.required(QuestionPlaceholderDirective);

  public ngAfterViewInit(): void {
    this.createQuestionTableListSubscriber();
  }

  /**
   * Replaces the table placeholders with newly Injected QuestionTableComponents.
   */
  private injectQuestionTables(): void {
    const instances: QuestionTableComponent[] =
      this.dynamicComponentLoader.injectReplacementComponentByElement<QuestionTableComponent>(
        this.questionDisplayPlaceholder.viewContainerRef,
        QuestionTableComponent,
        '.question-table-placeholder',
      );

    instances.forEach((instance, key) => {
      instance.setData(this.questionTableDataContainer.questionTableData[key]);
    });
  }

  /**
   * Subscribes to the queryList to get the questionDisplayPlaceholder instance
   * as soon as it's available.
   */
  private createQuestionTableListSubscriber(): void {
    this.questionDisplayPlaceholderList.changes
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.questionDisplayPlaceholderList),
        filter((list) => !!list.first),
      )
      .subscribe((list) => {
        this.questionDisplayPlaceholder = list.first;
        this.questionDisplayPlaceholder.ready().then(() => {
          this.injectQuestionTables();
        });
      });
  }
}
